/**
 * Time-horizon buckets for match lists (keep aligned with
 * `backend/utils/matchOrdering/timeHorizon.js`).
 *
 * Upcoming sort: imminent (≤15m) → horizon → competition priority → start time.
 * Horizons are loaded from `/trade/homepage-match-centre-mix` into the module cache.
 */

export type ListHorizons = {
    nearHours: number;
    midHours: number;
};

export const DEFAULT_LIST_HORIZONS: ListHorizons = Object.freeze({
    nearHours: 24,
    midHours: 168,
});

export const MATCH_TIME_HORIZON_NEAR_HOURS = DEFAULT_LIST_HORIZONS.nearHours;
export const MATCH_TIME_HORIZON_MID_HOURS = DEFAULT_LIST_HORIZONS.midHours;

/** Kickoffs within this window sort above near/mid/later (after true live). */
export const MATCH_IMMINENT_MINUTES = 15;

/** True when kickoff is still in the future and within the imminent window. */
export function isImminentKickoff(
    startTimeMs: number | null | undefined,
    nowMs: number = Date.now(),
    minutes: number = MATCH_IMMINENT_MINUTES,
): boolean {
    if (startTimeMs == null || !Number.isFinite(startTimeMs) || startTimeMs <= 0) return false;
    const minsUntil = (startTimeMs - nowMs) / (1000 * 60);
    return minsUntil > 0 && minsUntil <= minutes;
}

let cachedHorizons: ListHorizons = { ...DEFAULT_LIST_HORIZONS };

export function resolveListHorizons(
    raw?: Partial<ListHorizons> | null,
    fallback: ListHorizons = DEFAULT_LIST_HORIZONS,
): ListHorizons {
    const base = fallback || DEFAULT_LIST_HORIZONS;
    const src = raw && typeof raw === "object" ? raw : {};
    let nearHours = Math.min(Math.max(Number(src.nearHours ?? base.nearHours) || 24, 1), 72);
    let midHours = Math.min(Math.max(Number(src.midHours ?? base.midHours) || 168, 2), 336);
    if (midHours <= nearHours) {
        midHours = Math.min(336, nearHours + 24);
    }
    return { nearHours, midHours };
}

export function getActiveListHorizons(): ListHorizons {
    return cachedHorizons;
}

export function setActiveListHorizons(raw?: Partial<ListHorizons> | null): ListHorizons {
    cachedHorizons = resolveListHorizons(raw);
    return cachedHorizons;
}

export function listHorizonLabels(horizons: ListHorizons = getActiveListHorizons()): [string, string, string] {
    const h = resolveListHorizons(horizons);
    const midDays = h.midHours % 24 === 0 ? h.midHours / 24 : null;
    const midLabel = midDays != null ? `≤${midDays}d` : `≤${h.midHours}h`;
    return [`≤${h.nearHours}h`, midLabel, "Later"];
}

/**
 * @returns 0 = near (incl. past/overdue), 1 = mid, 2 = later / unknown
 */
export function getMatchTimeHorizonBucket(
    startTimeMs: number | null | undefined,
    nowMs: number = Date.now(),
    horizons: ListHorizons = getActiveListHorizons(),
): 0 | 1 | 2 {
    if (startTimeMs == null || !Number.isFinite(startTimeMs) || startTimeMs <= 0) return 2;
    const h = resolveListHorizons(horizons);
    const hoursUntil = (startTimeMs - nowMs) / (1000 * 60 * 60);
    if (hoursUntil <= h.nearHours) return 0;
    if (hoursUntil <= h.midHours) return 1;
    return 2;
}

export function getMatchTimeHorizonLabel(
    startTimeMs: number | null | undefined,
    nowMs: number = Date.now(),
    horizons: ListHorizons = getActiveListHorizons(),
): string {
    const labels = listHorizonLabels(horizons);
    return labels[getMatchTimeHorizonBucket(startTimeMs, nowMs, horizons)] ?? "Later";
}

/** Compare two kickoffs by time horizon only (negative if a is nearer-bucket). */
export function compareMatchTimeHorizon(
    startA: number | null | undefined,
    startB: number | null | undefined,
    nowMs: number = Date.now(),
    horizons: ListHorizons = getActiveListHorizons(),
): number {
    return (
        getMatchTimeHorizonBucket(startA, nowMs, horizons) -
        getMatchTimeHorizonBucket(startB, nowMs, horizons)
    );
}

/** Compare two non-live kickoffs: imminent first, then near/mid/later horizon. */
export function compareMatchUpcomingPhase(
    startA: number | null | undefined,
    startB: number | null | undefined,
    nowMs: number = Date.now(),
    horizons: ListHorizons = getActiveListHorizons(),
): number {
    const aImm = isImminentKickoff(startA, nowMs);
    const bImm = isImminentKickoff(startB, nowMs);
    if (aImm !== bImm) return aImm ? -1 : 1;
    if (aImm) return 0;
    return compareMatchTimeHorizon(startA, startB, nowMs, horizons);
}
